/**
 * Shared rich-cell renderers for the CERS matching grid AND the match dialog, so
 * both surfaces display CERS / Encamp facility data in the exact same block
 * format (team feedback: don't jar staff with a second layout in the modal).
 */

import CheckCircle from "@mui/icons-material/CheckCircle";
import { Box, Stack, Typography } from "@mui/material";

import { bestEpaId, Row } from "./matchRow";

/** Bold flags for the fields the CERS + Encamp cells share exactly (scannability). */
export type BoldFields = { epa: boolean; street: boolean };

/**
 * Structural shape any Encamp-facility source can satisfy — a suggestion
 * candidate, a fuzzy address match, or a manual search result. Fields the cell
 * doesn't get simply don't render.
 */
export type CandidateFacilityLike = {
  name?: string | null;
  streetAddress1?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  county?: string | null;
  epaIdStatuses?: { epaId: string; isPrimaryForFacility: boolean; isInactive: boolean }[] | null;
};

/** Compact address line for rich cells; bold when the two sides share the street. */
export function AddressLine({
  parts,
  bold,
}: {
  parts: (string | null | undefined)[];
  bold?: boolean;
}) {
  const formatted = parts.filter(Boolean).join(", ");
  if (!formatted) return null;
  return (
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ display: "block" }}
      fontWeight={bold ? "bold" : undefined}
    >
      {formatted}
    </Typography>
  );
}

/** CERS facility rich cell: name + address + county + epaId. */
export function CersFacilityCell({ row, bold }: { row: Row; bold?: BoldFields }) {
  return (
    <Box sx={{ py: 0.75 }}>
      <Typography variant="body1">{row.name ?? row.cersId}</Typography>
      <AddressLine parts={[row.siteAddress, row.city, row.zipCode]} bold={bold?.street} />
      {row.county && (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
          County: {row.county}
        </Typography>
      )}
      {row.epaId && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block" }}
          fontWeight={bold?.epa ? "bold" : undefined}
        >
          EPA: {row.epaId}
        </Typography>
      )}
    </Box>
  );
}

/** Encamp facility rich cell: name + address + county + primary EPA id. */
export function CandidateFacilityCell({
  candidate,
  bold,
}: {
  candidate: CandidateFacilityLike;
  bold?: BoldFields;
}) {
  const epaId = bestEpaId(candidate);
  return (
    <Box sx={{ py: 0.75 }}>
      <Typography variant="body1">{candidate.name}</Typography>
      <AddressLine
        parts={[candidate.streetAddress1, candidate.city, candidate.state, candidate.zip]}
        bold={bold?.street}
      />
      {candidate.county && (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
          County: {candidate.county}
        </Typography>
      )}
      {epaId && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block" }}
          fontWeight={bold?.epa ? "bold" : undefined}
        >
          EPA: {epaId}
        </Typography>
      )}
    </Box>
  );
}

/** Persistent "this is linked" indicator — the checkmark is the success signal. */
export function LinkedIndicator() {
  return (
    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ py: 0.75 }}>
      <CheckCircle fontSize="small" color="success" />
      <Typography variant="body2" color="success.main" fontWeight="medium">
        Linked
      </Typography>
    </Stack>
  );
}
